/**
 * CompactDdlShell — the popover-filling chrome every compact DDL surface
 * (editable arm AND read-only fallback card) mounts into: a `min-h-0 flex-1`
 * scrolling body plus ONE pinned action row that never scrolls.
 *
 * The row is the whole point of the split. Before it, the shared
 * [Design table] [Close] pair AND each form's own [Cancel] [Save …] pair
 * both lived inside the scroll body, so a long form pushed the popover's
 * actions below the fold and showed two competing action rows. Now the row
 * carries the shared pair on the left and, on the right, the portal target the
 * hosted form's buttons land in through `FormActionsSlot` — the form's fields
 * scroll, its buttons don't, and there is exactly one action row per arm.
 *
 * Split out of `CompactDdlEditor.tsx` (line-budget pass); it owns the three
 * testids below, which that module re-exports so importers are unaffected.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3).
 */
import { useState, type ReactNode } from 'react';

import { ScrollFade } from '../../visual-query-editor/render/common/ScrollFade';
import { FormActionsSlotProvider } from '../render/common/FormActionsSlot';
import { CompactDdlFooterActions } from './CompactDdlFooterActions';

/**
 * Testid of the host wrapper (scrolling arm body + the pinned action row).
 * Distinct from `COMPACT_DDL_EDITOR_TESTID`, which the `table` arm's embedded
 * `TableEditor` owns as its own root + per-facet testid prefix.
 */
export const COMPACT_DDL_EDITOR_HOST_TESTID = 'compact-ddl-editor-host';
/** The single pinned action row every compact DDL surface ends with. */
export const COMPACT_DDL_ACTIONS_TESTID = 'vqe-compact-ddl-actions';
/** Portal target inside that row for the arm's own [Cancel] [Save …] pair. */
export const COMPACT_DDL_FORM_ACTIONS_TESTID = 'vqe-compact-ddl-form-actions';
/** Fade overlay of the shell's own scroll body (distinct from the DML one). */
export const COMPACT_DDL_SCROLL_FADE_TESTID = 'vqe-compact-ddl-scroll-fade';

export type CompactDdlShellProps = React.ComponentProps<typeof CompactDdlFooterActions> & {
  /** Parked objectKind, surfaced as `data-object-kind` for the E2E suite. */
  readonly objectKind?: string | null;
  readonly children: ReactNode;
};

export function CompactDdlShell({ objectKind, children, ...footer }: CompactDdlShellProps) {
  // Callback ref (not `useRef`) so attaching the row re-renders the provider
  // and the portal actually lands — one commit, before paint.
  const [actionsRow, setActionsRow] = useState<HTMLDivElement | null>(null);
  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-testid={COMPACT_DDL_EDITOR_HOST_TESTID}
      data-object-kind={objectKind ?? undefined}
    >
      <ScrollFade
        className="min-h-0 flex-1"
        scrollClassName="overflow-x-hidden px-4 py-3"
        testId={COMPACT_DDL_SCROLL_FADE_TESTID}
      >
        <FormActionsSlotProvider target={actionsRow}>{children}</FormActionsSlotProvider>
      </ScrollFade>
      <div
        className="relative flex shrink-0 items-center gap-2 border-t border-border bg-popover px-4 py-2"
        data-testid={COMPACT_DDL_ACTIONS_TESTID}
      >
        <CompactDdlFooterActions {...footer} />
        <div
          ref={setActionsRow}
          className="ml-auto flex items-center gap-2"
          data-testid={COMPACT_DDL_FORM_ACTIONS_TESTID}
        />
      </div>
    </div>
  );
}
